import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Input, Select, Form, Row, Col, Checkbox } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { debounce } from 'lodash';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';
import InvoiceForm from './invoiceForm';
import MaterialFormModal from './materialFormModal';
import TransferModal from './transferModal';
import {
  formatLocationDisplay,
  formatMaterialIntegerDisplay,
  materialDisplayUnit,
} from '../utils';

const StorageTab = ({ dataVersion, onDataChange }) => {
  const [materials, setMaterials] = useState([]);
  const [registryOnlyRows, setRegistryOnlyRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ current: 1, pageSize: 10, total: 0 });
  const [sorter, setSorter] = useState({ order: 'desc', order_by: 'created_at' });
  const [search, setSearch] = useState({});
  const [showZero, setShowZero] = useState(false);

  const [showInvoice, setShowInvoice] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferMaterial, setTransferMaterial] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    loadCategories();
    loadWarehouses();
  }, []);

  useEffect(() => {
    loadMaterials();
  }, [meta.current, meta.pageSize, sorter, search.category_id, search.warehouse_id, showZero, dataVersion]);

  useEffect(() => {
    if (debounceRef.current) debounceRef.current.cancel();
    debounceRef.current = debounce(loadMaterials, 500);
    debounceRef.current();
    return () => debounceRef.current?.cancel();
  }, [search.name]);

  const loadMaterials = () => {
    const params = {
      page: meta.current,
      per: meta.pageSize,
      order: sorter.order,
      order_by: sorter.order_by,
      search: {
        name: search.name,
        category_id: search.category_id,
        warehouse_id: search.warehouse_id,
      },
    };
    if (!showZero) params.search.hide_zero = true;
    if (showZero) params.search.include_registry_without_wm = true;

    setLoading(true);
    Rest.get('/api/v1/warehouse_materials.json', { params })
      .then((r) => {
        setMaterials(r.data.warehouse_materials || []);
        setRegistryOnlyRows(r.data.registry_only_materials || []);
        setMeta((prev) => ({
          ...prev,
          total: r.data.meta?.total || 0,
          current: r.data.meta?.page || prev.current,
        }));
      })
      .finally(() => setLoading(false));
  };

  const loadCategories = () => {
    Rest.get('/api/v1/material_categories.json')
      .then((r) => setCategories(r.data.material_categories || []));
  };

  const loadWarehouses = () => {
    Rest.get('/api/v1/warehouses.json')
      .then((r) => setWarehouses(r.data.warehouses || []));
  };

  const handleTableChange = (pagination, _filters, sort) => {
    setMeta((prev) => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }));
    if (sort.column) {
      setSorter({
        order: sort.order === 'ascend' ? 'asc' : 'desc',
        order_by: sort.field,
      });
    }
  };

  const handleEdit = (record) => {
    setEditMaterial(record);
    setShowMaterialForm(true);
  };

  const handleTransfer = (record) => {
    setTransferMaterial(record);
    setShowTransfer(true);
  };

  const handleSuccess = () => {
    onDataChange();
    loadCategories();
    loadWarehouses();
  };

  const columns = [
    {
      title: 'Категория',
      dataIndex: 'category_name',
      width: '12%',
      render: (v) => v || '—',
    },
    { title: 'Наименование', dataIndex: 'material_name', sorter: true, width: '22%' },
    {
      title: 'Склад',
      dataIndex: 'warehouse_name',
      width: '10%',
      render: (v) => v || '—',
    },
    {
      title: 'Размещение', dataIndex: 'location', width: '15%',
      render: (loc, record) => {
        if (record.registry_only) return '—';
        return formatLocationDisplay(loc, record.temp_storage || {});
      },
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      sorter: true,
      width: '9%',
      render: (qty) => formatMaterialIntegerDisplay(qty),
    },
    {
      title: 'Ед. изм.',
      dataIndex: 'material_unit',
      width: '7%',
      render: (_, record) => materialDisplayUnit(record) || '—',
    },
    {
      title: '', dataIndex: 'actions', width: '14%',
      render: (_, record) => {
        if (record.registry_only) return null;
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button
              size="small"
              title="Переместить пользователю"
              icon={<FontAwesomeIcon icon={faUserPlus} />}
              onClick={() => handleTransfer(record)}
            />
            <Button
              size="small"
              title="Редактировать"
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => handleEdit(record)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Form layout="inline">
            <Form.Item>
              <Select
                value={search.category_id}
                onChange={(v) => setSearch((s) => ({ ...s, category_id: v }))}
                placeholder="Категория"
                style={{ width: 180 }}
                allowClear
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Form.Item>
              <Input
                value={search.name}
                onChange={(e) => setSearch((s) => ({ ...s, name: e.target.value }))}
                placeholder="Название"
                style={{ width: 200 }}
                allowClear
              />
            </Form.Item>
            <Form.Item>
              <Select
                value={search.warehouse_id}
                onChange={(v) => setSearch((s) => ({ ...s, warehouse_id: v }))}
                placeholder="Склад"
                style={{ width: 160 }}
                allowClear
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
            </Form.Item>
            <Form.Item>
              <Checkbox checked={showZero} onChange={(e) => setShowZero(e.target.checked)}>
                Показать все
              </Checkbox>
            </Form.Item>
          </Form>
        </Col>
        <Col>
          <Button type="primary" onClick={() => setShowInvoice(true)}>
            Поступление (накладная)
          </Button>
        </Col>
      </Row>

      <Preloader loading={loading}>
        <Table
          columns={columns}
          dataSource={
            showZero && registryOnlyRows.length > 0 && meta.current === 1
              ? [...registryOnlyRows, ...materials]
              : materials
          }
          rowKey={(r) => (r.registry_only ? `reg-${r.material_id}` : r.id)}
          size="small"
          pagination={{ ...meta, showSizeChanger: true }}
          onChange={handleTableChange}
        />
      </Preloader>

      <InvoiceForm
        visible={showInvoice}
        onClose={() => setShowInvoice(false)}
        onSuccess={handleSuccess}
      />

      <MaterialFormModal
        visible={showMaterialForm && !!editMaterial}
        material={editMaterial}
        onClose={() => { setShowMaterialForm(false); setEditMaterial(null); }}
        onSuccess={handleSuccess}
      />

      {transferMaterial && (
        <TransferModal
          visible={showTransfer}
          material={transferMaterial}
          onClose={() => { setShowTransfer(false); setTransferMaterial(null); }}
          onSuccess={handleSuccess}
        />
      )}
    </React.Fragment>
  );
};

export default StorageTab;
